import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '@/App';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Skeleton } from '@/components/ui/skeleton';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { toast } from 'sonner';
import { 
  Database, Users, TrendingUp, DollarSign, Save, RefreshCw, Plus, Trash2, 
  Edit, CheckCircle2, AlertTriangle, BarChart3, Calendar as CalendarIcon, Shield, Activity,
  FileSpreadsheet, Upload, Download, Clock, Sparkles, Gauge, Target, Zap, 
  CalendarDays, ChevronLeft, ChevronRight, Link2, Brain, Lightbulb, AlertCircle,
  FileUp, ArrowUpRight, ArrowDownRight, Minus, Info
} from 'lucide-react';
import { PageHeader } from '@/components/ModernUI';
import { format, startOfWeek, endOfWeek, addWeeks, subWeeks, getISOWeek, getYear } from 'date-fns';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// CSV Export Helper Function
const exportToCSV = async (token, exportType, filename) => {
  try {
    const res = await fetch(`${API_URL}/api/dashboard/export/${exportType}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    if (!res.ok) throw new Error('Export failed');
    
    const result = await res.json();
    const { data, columns } = result;
    
    if (!data || data.length === 0) {
      toast.error('No data to export');
      return;
    }
    
    // Create CSV content
    const csvRows = [];
    csvRows.push(columns.join(','));
    
    for (const row of data) {
      const values = columns.map(col => {
        const val = row[col];
        // Escape commas and quotes in values
        if (typeof val === 'string' && (val.includes(',') || val.includes('"'))) {
          return `"${val.replace(/"/g, '""')}"`;
        }
        return val ?? '';
      });
      csvRows.push(values.join(','));
    }
    
    const csvContent = csvRows.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${filename}_${format(new Date(), 'yyyy-MM-dd')}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    
    toast.success(`Exported ${data.length} records`);
  } catch (error) {
    console.error('Export error:', error);
    toast.error('Export failed');
  }
};

// Helper function to parse ISO week format
const parseISOWeek = (weekStr) => {
  if (!weekStr) return new Date();
  const match = weekStr.match(/(\d{4})-W(\d{2})/);
  if (!match) return new Date();
  const [, yearStr, weekStr2] = match;
  const year = parseInt(yearStr);
  const week = parseInt(weekStr2);
  const jan4 = new Date(year, 0, 4);
  const startOfYear = startOfWeek(jan4, { weekStartsOn: 1 });
  return addWeeks(startOfYear, week - 1);
};

// Week Picker Component
const WeekPicker = ({ value, onChange, label = "Select Week" }) => {
  const [date, setDate] = useState(() => parseISOWeek(value));
  const [open, setOpen] = useState(false);
  
  const weekStart = startOfWeek(date, { weekStartsOn: 1 });
  const weekEnd = endOfWeek(date, { weekStartsOn: 1 });
  const weekNumber = getISOWeek(date);
  const year = getYear(date);
  const weekId = `${year}-W${weekNumber.toString().padStart(2, '0')}`;
  
  const handleSelect = (newDate) => {
    if (newDate) {
      setDate(newDate);
      const wn = getISOWeek(newDate);
      const y = getYear(newDate);
      onChange(`${y}-W${wn.toString().padStart(2, '0')}`);
    }
  };
  
  useEffect(() => {
    if (value) {
      const parsed = parseISOWeek(value);
      if (!isNaN(parsed.getTime())) setDate(parsed);
    }
  }, [value]);
  
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" className="w-full justify-start text-left font-normal h-11">
          <CalendarDays className="mr-2 h-4 w-4" />
          <span className="flex-1">Week {weekNumber}, {year}</span>
          <span className="text-xs text-muted-foreground ml-2">
            {format(weekStart, 'MMM d')} - {format(weekEnd, 'MMM d')}
          </span>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <div className="p-3 border-b flex items-center justify-between">
          <Button variant="ghost" size="icon" onClick={() => handleSelect(subWeeks(date, 1))}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <div className="text-center">
            <p className="font-semibold">Week {weekNumber}</p>
            <p className="text-xs text-muted-foreground">{format(weekStart, 'MMM d')} - {format(weekEnd, 'MMM d, yyyy')}</p>
          </div>
          <Button variant="ghost" size="icon" onClick={() => handleSelect(addWeeks(date, 1))}>
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
        <Calendar mode="single" selected={date} onSelect={(d) => { handleSelect(d); setOpen(false); }} initialFocus showWeekNumber />
        <div className="p-2 border-t bg-muted/50">
          <p className="text-xs text-center text-muted-foreground">Selected: <strong>{weekId}</strong></p>
        </div>
      </PopoverContent>
    </Popover>
  );
};

// CSV Upload Component
const CSVUploader = ({ onUpload, type, template }) => {
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(null);

  const parseCSV = (text) => {
    const lines = text.trim().split('\n');
    const headers = lines[0].split(',').map(h => h.trim().toLowerCase().replace(/\s+/g, '_'));
    const records = [];
    
    for (let i = 1; i < lines.length; i++) {
      const values = lines[i].split(',').map(v => v.trim());
      if (values.length === headers.length) {
        const record = {};
        headers.forEach((h, idx) => {
          const val = values[idx];
          record[h] = isNaN(val) ? val : parseFloat(val);
        });
        records.push(record);
      }
    }
    return records;
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const records = parseCSV(event.target.result);
        setPreview({ filename: file.name, records, count: records.length });
      } catch (error) {
        toast.error('Failed to parse CSV file');
      }
    };
    reader.readAsText(file);
  };

  const handleUpload = async () => {
    if (!preview?.records) return;
    setUploading(true);
    try {
      await onUpload(preview.records);
      setPreview(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    } finally {
      setUploading(false);
    }
  };

  const downloadTemplate = () => {
    const blob = new Blob([template], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${type}_template.csv`;
    a.click();
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <input type="file" accept=".csv" ref={fileInputRef} onChange={handleFileSelect} className="hidden" />
        <Button variant="outline" onClick={() => fileInputRef.current?.click()} className="gap-2">
          <FileUp className="h-4 w-4" /> Select CSV File
        </Button>
        <Button variant="ghost" size="sm" onClick={downloadTemplate} className="gap-1 text-xs">
          <Download className="h-3 w-3" /> Template
        </Button>
      </div>
      
      {preview && (
        <div className="p-4 rounded-lg border bg-muted/30">
          <div className="flex items-center justify-between mb-3">
            <div>
              <p className="font-medium">{preview.filename}</p>
              <p className="text-sm text-muted-foreground">{preview.count} records found</p>
            </div>
            <Button onClick={handleUpload} disabled={uploading} className="gap-2">
              {uploading ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
              Import {preview.count} Records
            </Button>
          </div>
          <div className="max-h-32 overflow-auto text-xs font-mono bg-background rounded p-2">
            {JSON.stringify(preview.records.slice(0, 3), null, 2)}
            {preview.count > 3 && <p className="text-muted-foreground mt-1">...and {preview.count - 3} more</p>}
          </div>
        </div>
      )}
    </div>
  );
};

// AI Insights Panel
const AIInsightsPanel = ({ token }) => {
  const [insights, setInsights] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchInsights = useCallback(async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_URL}/api/dashboard/ai-insights`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setInsights(data);
      }
    } catch (error) {
      console.error('Failed to fetch AI insights');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchInsights();
  }, [fetchInsights]);

  if (loading) return <Skeleton className="h-64 w-full" />;

  const getSeverityIcon = (type, severity) => {
    if (type === 'success') return <CheckCircle2 className="h-4 w-4 text-emerald-500" />;
    if (type === 'critical' || severity === 'critical') return <AlertCircle className="h-4 w-4 text-red-500" />;
    if (type === 'warning') return <AlertTriangle className="h-4 w-4 text-amber-500" />;
    return <Info className="h-4 w-4 text-blue-500" />;
  };

  const getPriorityColor = (priority) => {
    if (priority === 'critical') return 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400';
    if (priority === 'high') return 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400';
    if (priority === 'medium') return 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400';
    return 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-400';
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Brain className="h-5 w-5 text-violet-500" />
              AI Insights & Recommendations
            </CardTitle>
            <CardDescription>AI-powered analysis of your operational data</CardDescription>
          </div>
          <div className="flex items-center gap-2">
            {insights?.summary && (
              <Badge className={insights.summary.health_score >= 80 ? 'bg-emerald-100 text-emerald-700' : insights.summary.health_score >= 60 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}>
                Health Score: {insights.summary.health_score}%
              </Badge>
            )}
            <Button size="sm" variant="outline" onClick={fetchInsights}>
              <RefreshCw className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {!insights?.insights?.length && !insights?.recommendations?.length ? (
          <div className="text-center py-8">
            <Lightbulb className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
            <p className="text-muted-foreground">Add more data to generate AI insights.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Insights Column */}
            <div>
              <h4 className="font-semibold mb-3 flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-amber-500" />
                Key Insights ({insights?.insights?.length || 0})
              </h4>
              <ScrollArea className="h-80">
                <div className="space-y-3 pr-4">
                  {insights?.insights?.map((insight, idx) => (
                    <div key={idx} className={`p-3 rounded-lg border ${insight.type === 'success' ? 'bg-emerald-50 border-emerald-200 dark:bg-emerald-900/10 dark:border-emerald-800' : insight.type === 'critical' ? 'bg-red-50 border-red-200 dark:bg-red-900/10 dark:border-red-800' : insight.type === 'warning' ? 'bg-amber-50 border-amber-200 dark:bg-amber-900/10 dark:border-amber-800' : 'bg-blue-50 border-blue-200 dark:bg-blue-900/10 dark:border-blue-800'}`}>
                      <div className="flex items-start gap-2">
                        {getSeverityIcon(insight.type, insight.severity)}
                        <div className="flex-1">
                          <p className="text-sm font-medium">{insight.message}</p>
                          <Badge variant="outline" className="mt-1 text-xs">{insight.category}</Badge>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            </div>

            {/* Recommendations Column */}
            <div>
              <h4 className="font-semibold mb-3 flex items-center gap-2">
                <Lightbulb className="h-4 w-4 text-blue-500" />
                Recommendations ({insights?.recommendations?.length || 0})
              </h4>
              <ScrollArea className="h-80">
                <div className="space-y-3 pr-4">
                  {insights?.recommendations?.map((rec, idx) => (
                    <div key={idx} className="p-3 rounded-lg border bg-card">
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <Badge className={`${getPriorityColor(rec.priority)} text-xs`}>
                          {rec.priority}
                        </Badge>
                        <Badge variant="outline" className="text-xs">{rec.category}</Badge>
                      </div>
                      <p className="text-sm font-medium mb-1">{rec.action}</p>
                      {rec.expected_impact && (
                        <p className="text-xs text-muted-foreground">
                          <ArrowUpRight className="h-3 w-3 inline mr-1" />
                          {rec.expected_impact}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </ScrollArea>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// Census Section with Bulk Import
const CensusTrendSection = ({ token }) => {
  const [trendData, setTrendData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showBulkDialog, setShowBulkDialog] = useState(false);
  const [saving, setSaving] = useState(false);
  const [newWeek, setNewWeek] = useState({ week: `${getYear(new Date())}-W${getISOWeek(new Date()).toString().padStart(2, '0')}`, adc: 0, admissions: 0, discharges: 0 });

  const fetchTrendData = useCallback(async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_URL}/api/dashboard/charts/trend-data?weeks=12`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) {
        const data = await res.json();
        setTrendData(data.trend_data || []);
      }
    } catch (error) {
      toast.error('Failed to load trend data');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchTrendData(); }, [fetchTrendData]);

  const handleSaveWeek = async (weekData) => {
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/dashboard/charts/trend-data`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ trend_data: [weekData] })
      });
      if (res.ok) {
        toast.success('Data synced across platform');
        fetchTrendData();
      }
    } finally {
      setSaving(false);
    }
  };

  const handleBulkImport = async (records) => {
    const res = await fetch(`${API_URL}/api/dashboard/bulk-import/census`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ records })
    });
    if (res.ok) {
      const result = await res.json();
      toast.success(result.message);
      setShowBulkDialog(false);
      fetchTrendData();
    } else {
      toast.error('Bulk import failed');
    }
  };

  if (loading) return <Skeleton className="h-64 w-full" />;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-indigo-500" />
              Census & Admissions Data
            </CardTitle>
            <CardDescription>Weekly census data - syncs to Dashboard, Predictions, Strategic Planning</CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <Dialog open={showBulkDialog} onOpenChange={setShowBulkDialog}>
              <DialogTrigger asChild>
                <Button size="sm" variant="outline" className="gap-1">
                  <FileUp className="h-4 w-4" /> Bulk Import
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg">
                <DialogHeader>
                  <DialogTitle>Bulk Import Census Data</DialogTitle>
                  <DialogDescription>Upload a CSV file with weekly census data</DialogDescription>
                </DialogHeader>
                <CSVUploader 
                  onUpload={handleBulkImport} 
                  type="census"
                  template="week,adc,admissions,discharges\n2025-W01,180,15,12\n2025-W02,183,18,15"
                />
              </DialogContent>
            </Dialog>
            <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
              <DialogTrigger asChild>
                <Button size="sm" className="gap-1"><Plus className="h-4 w-4" /> Add Week</Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Add Weekly Census Data</DialogTitle>
                </DialogHeader>
                <div className="grid gap-4 py-4">
                  <WeekPicker value={newWeek.week} onChange={(week) => setNewWeek({ ...newWeek, week })} />
                  <div className="grid grid-cols-3 gap-4">
                    <div><Label>ADC</Label><Input type="number" value={newWeek.adc} onChange={(e) => setNewWeek({ ...newWeek, adc: parseFloat(e.target.value) || 0 })} /></div>
                    <div><Label>Admissions</Label><Input type="number" value={newWeek.admissions} onChange={(e) => setNewWeek({ ...newWeek, admissions: parseInt(e.target.value) || 0 })} /></div>
                    <div><Label>Discharges</Label><Input type="number" value={newWeek.discharges} onChange={(e) => setNewWeek({ ...newWeek, discharges: parseInt(e.target.value) || 0 })} /></div>
                  </div>
                </div>
                <DialogFooter>
                  <Button onClick={() => { handleSaveWeek(newWeek); setShowAddDialog(false); }} disabled={saving}>
                    {saving ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />} Save
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
            <Button size="sm" variant="outline" onClick={() => exportToCSV(token, 'census', 'census_data')} className="gap-1" data-testid="export-census-btn">
              <Download className="h-4 w-4" /> Export
            </Button>
            <Button size="sm" variant="outline" onClick={fetchTrendData}><RefreshCw className="h-4 w-4" /></Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {trendData.length === 0 ? (
          <div className="text-center py-8">
            <BarChart3 className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
            <p className="text-muted-foreground">No data yet. Add weekly data or use bulk import.</p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Week</TableHead>
                <TableHead className="text-right">ADC</TableHead>
                <TableHead className="text-right">Admissions</TableHead>
                <TableHead className="text-right">Discharges</TableHead>
                <TableHead className="text-right">Net</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {trendData.map((week, idx) => (
                <TableRow key={idx}>
                  <TableCell className="font-medium">{week.week || week.name}</TableCell>
                  <TableCell className="text-right">{week.adc}</TableCell>
                  <TableCell className="text-right text-emerald-600">+{week.admissions}</TableCell>
                  <TableCell className="text-right text-rose-600">-{week.discharges}</TableCell>
                  <TableCell className={`text-right font-semibold ${(week.admissions - week.discharges) >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                    {week.admissions - week.discharges >= 0 ? '+' : ''}{week.admissions - week.discharges}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
};

// Staff Distribution Section
const StaffDistributionSection = ({ token }) => {
  const [disciplineData, setDisciplineData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [editedData, setEditedData] = useState([]);
  const [saving, setSaving] = useState(false);
  const [showBulkDialog, setShowBulkDialog] = useState(false);

  const fetchDisciplineData = useCallback(async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_URL}/api/dashboard/charts/discipline-breakdown`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) {
        const data = await res.json();
        setDisciplineData(data.discipline_data || []);
        setEditedData(data.discipline_data || []);
      }
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchDisciplineData(); }, [fetchDisciplineData]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const disciplines = editedData.map(d => ({ name: d.name, count: d.value }));
      const res = await fetch(`${API_URL}/api/dashboard/charts/discipline-breakdown`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ disciplines })
      });
      if (res.ok) {
        toast.success('Staff distribution synced');
        setEditMode(false);
        fetchDisciplineData();
      }
    } finally {
      setSaving(false);
    }
  };

  const handleBulkImport = async (records) => {
    const res = await fetch(`${API_URL}/api/dashboard/bulk-import/staff`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ records })
    });
    if (res.ok) {
      toast.success('Staff data imported');
      setShowBulkDialog(false);
      fetchDisciplineData();
    }
  };

  const disciplineColors = { 'RN': '#0f172a', 'LPN': '#0d9488', 'SW': '#d97706', 'Aide': '#6366f1', 'Chaplain': '#8b5cf6', 'Volunteer': '#ec4899', 'Other': '#94a3b8' };

  if (loading) return <Skeleton className="h-64 w-full" />;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2"><Users className="h-5 w-5 text-blue-500" />Staff Distribution</CardTitle>
            <CardDescription>Staff by discipline - syncs to Dashboard, Workforce Intelligence</CardDescription>
          </div>
          <div className="flex gap-2">
            <Dialog open={showBulkDialog} onOpenChange={setShowBulkDialog}>
              <DialogTrigger asChild>
                <Button size="sm" variant="outline" className="gap-1"><FileUp className="h-4 w-4" /> Bulk Import</Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg">
                <DialogHeader><DialogTitle>Bulk Import Staff Data</DialogTitle></DialogHeader>
                <CSVUploader onUpload={handleBulkImport} type="staff" template="name,count\nRN,15\nLPN,8\nSW,5\nAide,12" />
              </DialogContent>
            </Dialog>
            <Button size="sm" variant="outline" onClick={() => exportToCSV(token, 'staff', 'staff_distribution')} className="gap-1" data-testid="export-staff-btn">
              <Download className="h-4 w-4" /> Export
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {(editMode ? editedData : disciplineData).map((disc) => (
            <div key={disc.name} className="flex items-center gap-3 p-4 rounded-xl border">
              <div className="h-10 w-10 rounded-lg flex items-center justify-center text-white font-bold text-sm" style={{ backgroundColor: disciplineColors[disc.name] || disc.color }}>
                {disc.name.substring(0, 2)}
              </div>
              <div className="flex-1">
                <p className="font-medium">{disc.name}</p>
                {editMode ? (
                  <Input type="number" value={disc.value} onChange={(e) => setEditedData(prev => prev.map(d => d.name === disc.name ? { ...d, value: parseInt(e.target.value) || 0 } : d))} className="h-8 mt-1" />
                ) : (
                  <p className="text-2xl font-bold">{disc.value}</p>
                )}
              </div>
            </div>
          ))}
        </div>
        <div className="flex justify-end gap-2 mt-4 pt-4 border-t">
          {editMode ? (
            <>
              <Button variant="outline" onClick={() => { setEditMode(false); setEditedData(disciplineData); }}>Cancel</Button>
              <Button onClick={handleSave} disabled={saving}>{saving ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}Save</Button>
            </>
          ) : (
            <Button onClick={() => setEditMode(true)}><Edit className="h-4 w-4 mr-2" />Edit</Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

// Productivity Section
const ProductivitySection = ({ token }) => {
  const [productivityData, setProductivityData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showBulkDialog, setShowBulkDialog] = useState(false);
  const [saving, setSaving] = useState(false);
  const [newRecord, setNewRecord] = useState({ week: `${getYear(new Date())}-W${getISOWeek(new Date()).toString().padStart(2, '0')}`, clinician_name: '', discipline: 'RN', visits_completed: 0, visit_capacity: 25, patients_assigned: 0, hours_worked: 40 });

  const fetchProductivityData = useCallback(async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_URL}/api/dashboard/charts/productivity?weeks=8`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) {
        const data = await res.json();
        setProductivityData(data.productivity_data || []);
      }
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchProductivityData(); }, [fetchProductivityData]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/dashboard/charts/productivity`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(newRecord)
      });
      if (res.ok) {
        toast.success('Productivity data synced');
        setShowAddDialog(false);
        fetchProductivityData();
      }
    } finally {
      setSaving(false);
    }
  };

  const handleBulkImport = async (records) => {
    const res = await fetch(`${API_URL}/api/dashboard/bulk-import/productivity`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ records })
    });
    if (res.ok) {
      toast.success('Productivity data imported');
      setShowBulkDialog(false);
      fetchProductivityData();
    }
  };

  if (loading) return <Skeleton className="h-64 w-full" />;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2"><Gauge className="h-5 w-5 text-orange-500" />Staff Productivity</CardTitle>
            <CardDescription>Visit capacity and productivity - syncs to RNCM, Burnout Monitoring</CardDescription>
          </div>
          <div className="flex gap-2">
            <Dialog open={showBulkDialog} onOpenChange={setShowBulkDialog}>
              <DialogTrigger asChild><Button size="sm" variant="outline" className="gap-1"><FileUp className="h-4 w-4" /> Bulk</Button></DialogTrigger>
              <DialogContent><DialogHeader><DialogTitle>Bulk Import Productivity</DialogTitle></DialogHeader>
                <CSVUploader onUpload={handleBulkImport} type="productivity" template="week,clinician_name,discipline,visits_completed,visit_capacity,patients_assigned,hours_worked\n2025-W01,Jane RN,RN,22,25,12,40" />
              </DialogContent>
            </Dialog>
            <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
              <DialogTrigger asChild><Button size="sm" className="gap-1"><Plus className="h-4 w-4" /> Add</Button></DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>Add Productivity Record</DialogTitle></DialogHeader>
                <div className="grid gap-4 py-4">
                  <WeekPicker value={newRecord.week} onChange={(week) => setNewRecord({ ...newRecord, week })} />
                  <div className="grid grid-cols-2 gap-4">
                    <div><Label>Clinician</Label><Input value={newRecord.clinician_name} onChange={(e) => setNewRecord({ ...newRecord, clinician_name: e.target.value })} /></div>
                    <div><Label>Discipline</Label>
                      <Select value={newRecord.discipline} onValueChange={(v) => setNewRecord({ ...newRecord, discipline: v })}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="RN">RN</SelectItem><SelectItem value="LPN">LPN</SelectItem><SelectItem value="SW">SW</SelectItem><SelectItem value="Aide">Aide</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div><Label>Visits</Label><Input type="number" value={newRecord.visits_completed} onChange={(e) => setNewRecord({ ...newRecord, visits_completed: parseInt(e.target.value) || 0 })} /></div>
                    <div><Label>Capacity</Label><Input type="number" value={newRecord.visit_capacity} onChange={(e) => setNewRecord({ ...newRecord, visit_capacity: parseInt(e.target.value) || 0 })} /></div>
                  </div>
                </div>
                <DialogFooter><Button onClick={handleSave} disabled={saving}>{saving ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}Save</Button></DialogFooter>
              </DialogContent>
            </Dialog>
            <Button size="sm" variant="outline" onClick={() => exportToCSV(token, 'productivity', 'productivity_data')} className="gap-1" data-testid="export-productivity-btn">
              <Download className="h-4 w-4" /> Export
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {productivityData.length === 0 ? (
          <div className="text-center py-8"><Gauge className="h-12 w-12 text-muted-foreground mx-auto mb-3" /><p className="text-muted-foreground">No data. Add records or use bulk import.</p></div>
        ) : (
          <div className="space-y-3">
            {productivityData.map((week, idx) => (
              <div key={idx} className="p-4 rounded-lg border">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-semibold">{week.week}</span>
                  <Badge className={week.avg_productivity >= 85 ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}>{week.avg_productivity}%</Badge>
                </div>
                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div><p className="text-muted-foreground">Visits</p><p className="font-semibold">{week.total_visits}</p></div>
                  <div><p className="text-muted-foreground">Capacity</p><p className="font-semibold">{week.total_capacity}</p></div>
                  <div><p className="text-muted-foreground">Records</p><p className="font-semibold">{week.records?.length || 0}</p></div>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// Capacity Section
const CapacitySection = ({ token }) => {
  const [capacityData, setCapacityData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [saving, setSaving] = useState(false);
  const [newRecord, setNewRecord] = useState({ week: `${getYear(new Date())}-W${getISOWeek(new Date()).toString().padStart(2, '0')}`, current_capacity: 0, target_capacity: 0, projected_demand: 0, open_positions: 0 });

  const fetchCapacityData = useCallback(async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_URL}/api/dashboard/charts/capacity?weeks=8`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) {
        const data = await res.json();
        setCapacityData(data.capacity_data || []);
      }
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchCapacityData(); }, [fetchCapacityData]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/dashboard/charts/capacity`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(newRecord)
      });
      if (res.ok) {
        toast.success('Capacity data synced');
        setShowAddDialog(false);
        fetchCapacityData();
      }
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Skeleton className="h-64 w-full" />;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2"><Target className="h-5 w-5 text-violet-500" />Capacity Planning</CardTitle>
            <CardDescription>Staff capacity - syncs to Hiring Predictions, Workforce Planning</CardDescription>
          </div>
          <div className="flex gap-2">
            <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
              <DialogTrigger asChild><Button size="sm" className="gap-1"><Plus className="h-4 w-4" /> Add</Button></DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>Add Capacity Record</DialogTitle></DialogHeader>
                <div className="grid gap-4 py-4">
                  <WeekPicker value={newRecord.week} onChange={(week) => setNewRecord({ ...newRecord, week })} />
                  <div className="grid grid-cols-2 gap-4">
                    <div><Label>Current FTE</Label><Input type="number" value={newRecord.current_capacity} onChange={(e) => setNewRecord({ ...newRecord, current_capacity: parseInt(e.target.value) || 0 })} /></div>
                    <div><Label>Target FTE</Label><Input type="number" value={newRecord.target_capacity} onChange={(e) => setNewRecord({ ...newRecord, target_capacity: parseInt(e.target.value) || 0 })} /></div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div><Label>Projected Demand</Label><Input type="number" value={newRecord.projected_demand} onChange={(e) => setNewRecord({ ...newRecord, projected_demand: parseInt(e.target.value) || 0 })} /></div>
                    <div><Label>Open Positions</Label><Input type="number" value={newRecord.open_positions} onChange={(e) => setNewRecord({ ...newRecord, open_positions: parseInt(e.target.value) || 0 })} /></div>
                  </div>
                </div>
                <DialogFooter><Button onClick={handleSave} disabled={saving}>{saving ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}Save</Button></DialogFooter>
              </DialogContent>
            </Dialog>
            <Button size="sm" variant="outline" onClick={() => exportToCSV(token, 'capacity', 'capacity_data')} className="gap-1" data-testid="export-capacity-btn">
              <Download className="h-4 w-4" /> Export
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {capacityData.length === 0 ? (
          <div className="text-center py-8"><Target className="h-12 w-12 text-muted-foreground mx-auto mb-3" /><p className="text-muted-foreground">No data. Add capacity records.</p></div>
        ) : (
          <Table>
            <TableHeader><TableRow><TableHead>Week</TableHead><TableHead className="text-right">Current</TableHead><TableHead className="text-right">Target</TableHead><TableHead className="text-right">Utilization</TableHead><TableHead className="text-right">Gap</TableHead></TableRow></TableHeader>
            <TableBody>
              {capacityData.map((row, idx) => (
                <TableRow key={idx}>
                  <TableCell className="font-medium">{row.week}</TableCell>
                  <TableCell className="text-right">{row.current_capacity}</TableCell>
                  <TableCell className="text-right">{row.target_capacity}</TableCell>
                  <TableCell className="text-right"><Badge className={row.capacity_utilization > 100 ? 'bg-rose-100 text-rose-700' : 'bg-emerald-100 text-emerald-700'}>{row.capacity_utilization}%</Badge></TableCell>
                  <TableCell className={`text-right font-semibold ${row.gap > 0 ? 'text-rose-600' : 'text-emerald-600'}`}>{row.gap > 0 ? `+${row.gap}` : row.gap}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
};

// Financials Section
const FinancialsSection = ({ token }) => {
  const [financialData, setFinancialData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showBulkDialog, setShowBulkDialog] = useState(false);
  const [saving, setSaving] = useState(false);
  const [newRecord, setNewRecord] = useState({ month: format(new Date(), 'yyyy-MM'), revenue: 0, payroll_wages: 0, pharmacy: 0, dme: 0, supplies: 0, operating_cost: 0, facility_cost: 0, adc: 0, patients_served: 0 });

  const fetchFinancialData = useCallback(async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_URL}/api/dashboard/charts/financials?months=12`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) {
        const data = await res.json();
        setFinancialData(data.financial_data || []);
      }
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchFinancialData(); }, [fetchFinancialData]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/dashboard/charts/financials`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(newRecord)
      });
      if (res.ok) {
        toast.success('Financial data saved');
        setShowAddDialog(false);
        fetchFinancialData();
      }
    } finally {
      setSaving(false);
    }
  };

  const handleBulkImport = async (records) => {
    const res = await fetch(`${API_URL}/api/dashboard/bulk-import/financials`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ records })
    });
    if (res.ok) {
      toast.success('Financial data imported');
      setShowBulkDialog(false);
      fetchFinancialData();
    }
  };

  const formatCurrency = (val) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(val || 0);

  if (loading) return <Skeleton className="h-64 w-full" />;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2"><DollarSign className="h-5 w-5 text-emerald-500" />Monthly Financials</CardTitle>
            <CardDescription>Revenue, expenses, and margin tracking</CardDescription>
          </div>
          <div className="flex gap-2">
            <Dialog open={showBulkDialog} onOpenChange={setShowBulkDialog}>
              <DialogTrigger asChild><Button size="sm" variant="outline" className="gap-1"><FileUp className="h-4 w-4" /> Bulk</Button></DialogTrigger>
              <DialogContent><DialogHeader><DialogTitle>Bulk Import Financials</DialogTitle></DialogHeader>
                <CSVUploader onUpload={handleBulkImport} type="financials" template="month,revenue,payroll_wages,pharmacy,dme,supplies,operating_cost,facility_cost,adc,patients_served\n2025-01,450000,275000,22500,22500,22500,31500,18000,180,200" />
              </DialogContent>
            </Dialog>
            <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
              <DialogTrigger asChild><Button size="sm" className="gap-1"><Plus className="h-4 w-4" /> Add Month</Button></DialogTrigger>
              <DialogContent className="max-w-2xl">
                <DialogHeader><DialogTitle>Add Monthly Financial Data</DialogTitle></DialogHeader>
                <div className="grid gap-4 py-4">
                  <div><Label>Month</Label><Input type="month" value={newRecord.month} onChange={(e) => setNewRecord({ ...newRecord, month: e.target.value })} /></div>
                  <div className="grid grid-cols-3 gap-4">
                    <div><Label>Revenue</Label><Input type="number" value={newRecord.revenue} onChange={(e) => setNewRecord({ ...newRecord, revenue: parseFloat(e.target.value) || 0 })} /></div>
                    <div><Label>Payroll</Label><Input type="number" value={newRecord.payroll_wages} onChange={(e) => setNewRecord({ ...newRecord, payroll_wages: parseFloat(e.target.value) || 0 })} /></div>
                    <div><Label>Pharmacy</Label><Input type="number" value={newRecord.pharmacy} onChange={(e) => setNewRecord({ ...newRecord, pharmacy: parseFloat(e.target.value) || 0 })} /></div>
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    <div><Label>DME</Label><Input type="number" value={newRecord.dme} onChange={(e) => setNewRecord({ ...newRecord, dme: parseFloat(e.target.value) || 0 })} /></div>
                    <div><Label>Supplies</Label><Input type="number" value={newRecord.supplies} onChange={(e) => setNewRecord({ ...newRecord, supplies: parseFloat(e.target.value) || 0 })} /></div>
                    <div><Label>Operating</Label><Input type="number" value={newRecord.operating_cost} onChange={(e) => setNewRecord({ ...newRecord, operating_cost: parseFloat(e.target.value) || 0 })} /></div>
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    <div><Label>Facility</Label><Input type="number" value={newRecord.facility_cost} onChange={(e) => setNewRecord({ ...newRecord, facility_cost: parseFloat(e.target.value) || 0 })} /></div>
                    <div><Label>ADC</Label><Input type="number" value={newRecord.adc} onChange={(e) => setNewRecord({ ...newRecord, adc: parseFloat(e.target.value) || 0 })} /></div>
                    <div><Label>Patients</Label><Input type="number" value={newRecord.patients_served} onChange={(e) => setNewRecord({ ...newRecord, patients_served: parseInt(e.target.value) || 0 })} /></div>
                  </div>
                </div>
                <DialogFooter><Button onClick={handleSave} disabled={saving}>{saving ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}Save</Button></DialogFooter>
              </DialogContent>
            </Dialog>
            <Button size="sm" variant="outline" onClick={() => exportToCSV(token, 'financials', 'financials_data')} className="gap-1" data-testid="export-financials-btn">
              <Download className="h-4 w-4" /> Export
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {financialData.length === 0 ? (
          <div className="text-center py-8"><DollarSign className="h-12 w-12 text-muted-foreground mx-auto mb-3" /><p className="text-muted-foreground">No financial data. Add monthly records.</p></div>
        ) : (
          <Table>
            <TableHeader><TableRow><TableHead>Month</TableHead><TableHead className="text-right">Revenue</TableHead><TableHead className="text-right">Expenses</TableHead><TableHead className="text-right">Net Profit</TableHead><TableHead className="text-right">Margin</TableHead></TableRow></TableHeader>
            <TableBody>
              {financialData.map((row, idx) => (
                <TableRow key={idx}>
                  <TableCell className="font-medium">{row.month}</TableCell>
                  <TableCell className="text-right text-emerald-600">{formatCurrency(row.revenue)}</TableCell>
                  <TableCell className="text-right text-rose-600">{formatCurrency(row.total_expenses)}</TableCell>
                  <TableCell className={`text-right font-semibold ${row.net_profit >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>{formatCurrency(row.net_profit)}</TableCell>
                  <TableCell className="text-right"><Badge className={row.net_margin_percent >= 12 ? 'bg-emerald-100 text-emerald-700' : row.net_margin_percent >= 8 ? 'bg-amber-100 text-amber-700' : 'bg-rose-100 text-rose-700'}>{row.net_margin_percent?.toFixed(1)}%</Badge></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
};

// Main Data Admin Panel
export default function DataAdminPanel() {
  const { token, user } = useAuth();
  const [activeTab, setActiveTab] = useState('insights');
  const [syncing, setSyncing] = useState(false);
  
  const canEdit = user?.role === 'super_admin' || user?.role === 'admin';

  const handleFullSync = async () => {
    setSyncing(true);
    try {
      const res = await fetch(`${API_URL}/api/data-sync/full-sync`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const result = await res.json();
        toast.success(`Synced ${result.results?.total_synced || 0} records across all platforms`);
      } else {
        toast.error('Sync failed');
      }
    } catch (error) {
      toast.error('Sync failed: ' + error.message);
    } finally {
      setSyncing(false);
    }
  };

  if (!canEdit) {
    return (
      <div className="space-y-6">
        <PageHeader icon={Database} title="Data Admin Panel" subtitle="Manage operational data" gradient="from-violet-600 via-purple-600 to-indigo-600" />
        <Card className="p-12 text-center bg-slate-900/80 backdrop-blur-xl border-slate-700">
          <Shield className="h-16 w-16 mx-auto text-slate-500 mb-4" />
          <h3 className="text-xl font-bold mb-2 text-white">Admin Access Required</h3>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="data-admin-panel">
      {/* Futuristic Neon Header */}
      <div className="relative overflow-hidden rounded-3xl p-8 text-white">
        {/* Animated gradient background */}
        <div className="absolute inset-0 bg-gradient-to-r from-violet-600 via-purple-600 to-fuchsia-600 animate-gradient-x" />
        
        {/* Mesh overlay */}
        <div className="absolute inset-0 opacity-20" style={{
          backgroundImage: `radial-gradient(circle at 25% 25%, white 1px, transparent 1px),
                            radial-gradient(circle at 75% 75%, white 1px, transparent 1px)`,
          backgroundSize: '60px 60px'
        }} />
        
        {/* Glow effects */}
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-white/20 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-64 h-64 bg-white/15 rounded-full blur-3xl" />

        <div className="relative z-10 flex flex-col lg:flex-row lg:items-center justify-between gap-6">
          <div>
            <h1 className="text-4xl font-black tracking-tight flex items-center gap-4">
              <div className="p-3 bg-white/20 rounded-2xl backdrop-blur-sm shadow-lg" style={{ boxShadow: '0 0 30px rgba(255,255,255,0.3)' }}>
                <Database className="h-8 w-8" style={{ filter: 'drop-shadow(0 0 10px white)' }} />
              </div>
              <span className="drop-shadow-lg" style={{ textShadow: '0 0 40px rgba(255,255,255,0.5)' }}>
                Data Admin Panel
              </span>
            </h1>
            <p className="text-white/80 mt-3 text-lg font-medium max-w-xl">
              Manage data with AI insights and cross-platform sync
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Button 
              size="sm" 
              onClick={handleFullSync} 
              disabled={syncing}
              className="gap-2 bg-white/10 hover:bg-white/20 text-white border border-white/20 font-bold"
              data-testid="full-sync-btn"
            >
              {syncing ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Link2 className="h-4 w-4" />}
              {syncing ? 'Syncing...' : 'Sync All Data'}
            </Button>
            <span className="px-3 py-1 rounded-full text-sm font-bold text-white bg-white/20 backdrop-blur-sm inline-flex items-center gap-1">
              <Shield className="h-3 w-3" /> Admin
            </span>
          </div>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="flex flex-wrap h-auto gap-2 p-2 bg-slate-900/60 backdrop-blur-xl rounded-2xl border border-slate-700/50">
          <TabsTrigger value="insights" className="gap-2 data-[state=active]:bg-gradient-to-r data-[state=active]:from-fuchsia-500 data-[state=active]:to-purple-600 data-[state=active]:text-white data-[state=active]:shadow-lg data-[state=active]:shadow-fuchsia-500/30 rounded-xl px-4 py-2 font-bold transition-all">
            <Brain className="h-4 w-4" /> AI Insights
          </TabsTrigger>
          <TabsTrigger value="census" className="gap-2 data-[state=active]:bg-gradient-to-r data-[state=active]:from-cyan-500 data-[state=active]:to-blue-600 data-[state=active]:text-white data-[state=active]:shadow-lg data-[state=active]:shadow-cyan-500/30 rounded-xl px-4 py-2 font-bold transition-all">
            <TrendingUp className="h-4 w-4" /> Census
          </TabsTrigger>
          <TabsTrigger value="staff" className="gap-2 data-[state=active]:bg-gradient-to-r data-[state=active]:from-emerald-500 data-[state=active]:to-teal-600 data-[state=active]:text-white data-[state=active]:shadow-lg data-[state=active]:shadow-emerald-500/30 rounded-xl px-4 py-2 font-bold transition-all">
            <Users className="h-4 w-4" /> Staff
          </TabsTrigger>
          <TabsTrigger value="productivity" className="gap-2 data-[state=active]:bg-gradient-to-r data-[state=active]:from-amber-500 data-[state=active]:to-orange-600 data-[state=active]:text-white data-[state=active]:shadow-lg data-[state=active]:shadow-amber-500/30 rounded-xl px-4 py-2 font-bold transition-all">
            <Gauge className="h-4 w-4" /> Productivity
          </TabsTrigger>
          <TabsTrigger value="capacity" className="gap-2 data-[state=active]:bg-gradient-to-r data-[state=active]:from-rose-500 data-[state=active]:to-pink-600 data-[state=active]:text-white data-[state=active]:shadow-lg data-[state=active]:shadow-rose-500/30 rounded-xl px-4 py-2 font-bold transition-all">
            <Target className="h-4 w-4" /> Capacity
          </TabsTrigger>
          <TabsTrigger value="financials" className="gap-2 data-[state=active]:bg-gradient-to-r data-[state=active]:from-violet-500 data-[state=active]:to-indigo-600 data-[state=active]:text-white data-[state=active]:shadow-lg data-[state=active]:shadow-violet-500/30 rounded-xl px-4 py-2 font-bold transition-all">
            <DollarSign className="h-4 w-4" /> Financials
          </TabsTrigger>
        </TabsList>

        <TabsContent value="insights"><AIInsightsPanel token={token} /></TabsContent>
        <TabsContent value="census"><CensusTrendSection token={token} /></TabsContent>
        <TabsContent value="staff"><StaffDistributionSection token={token} /></TabsContent>
        <TabsContent value="productivity"><ProductivitySection token={token} /></TabsContent>
        <TabsContent value="capacity"><CapacitySection token={token} /></TabsContent>
        <TabsContent value="financials"><FinancialsSection token={token} /></TabsContent>
      </Tabs>

      <Card className="bg-slate-900/60 backdrop-blur-xl border-cyan-500/30" style={{ boxShadow: '0 0 20px rgba(34, 211, 238, 0.1)' }}>
        <CardContent className="p-6">
          <div className="flex items-start gap-4">
            <div className="p-2 bg-cyan-500/20 rounded-xl">
              <Link2 className="h-5 w-5 text-cyan-400" style={{ filter: 'drop-shadow(0 0 5px rgba(34, 211, 238, 0.5))' }} />
            </div>
            <div>
              <h4 className="font-bold mb-1 text-white">Cross-Platform Data Sync</h4>
              <p className="text-sm text-slate-400 font-medium">All data syncs to: Dashboard Charts, Census Predictions, RNCM Productivity, Workforce Intelligence, Strategic Planning, Hiring Predictions</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
